'use client';

import { useState } from 'react';
import { PDFDocument, rgb, type PDFPage, type RGB } from 'pdf-lib';

type GraphType = 'grid' | 'dot' | 'lined' | 'isometric';
type Paper = 'letter' | 'a4';
type Orientation = 'portrait' | 'landscape';
type LineColor = 'grey' | 'blue';

const PAPER: Record<Paper, [number, number]> = { letter: [612, 792], a4: [595.28, 841.89] };
const MM = 2.834645669; // points per millimetre
const COLORS: Record<LineColor, RGB> = { grey: rgb(0.72, 0.74, 0.78), blue: rgb(0.62, 0.76, 0.9) };

function triggerDownload(bytes: Uint8Array, name: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Clip an infinite line y = m*x + b to a rectangle; returns the two endpoints or null.
function clipLine(m: number, b: number, x0: number, y0: number, x1: number, y1: number) {
  const pts: { x: number; y: number }[] = [];
  const add = (x: number, y: number) => {
    if (x >= x0 - 0.01 && x <= x1 + 0.01 && y >= y0 - 0.01 && y <= y1 + 0.01) pts.push({ x, y });
  };
  add(x0, m * x0 + b);
  add(x1, m * x1 + b);
  if (m !== 0) {
    add((y0 - b) / m, y0);
    add((y1 - b) / m, y1);
  }
  if (pts.length < 2) return null;
  return [pts[0], pts[1]] as const;
}

function drawDiagonals(
  page: PDFPage,
  rect: [number, number, number, number],
  m: number,
  perp: number,
  color: RGB,
) {
  const [x0, y0, x1, y1] = rect;
  const step = perp * Math.sqrt(1 + m * m); // vertical intercept spacing for perpendicular gap = perp
  const bMin = y0 - m * x1 - step;
  const bMax = y1 - m * x0 + step;
  for (let b = bMin; b <= bMax; b += step) {
    const seg = clipLine(m, b, x0, y0, x1, y1);
    if (seg) page.drawLine({ start: seg[0], end: seg[1], thickness: 0.5, color });
  }
}

function drawPage(
  pdf: PDFDocument,
  type: GraphType,
  spacingMm: number,
  w: number,
  h: number,
  color: RGB,
) {
  const page = pdf.addPage([w, h]);
  const margin = 8 * MM;
  const s = Math.max(1, spacingMm) * MM;
  const x0 = margin,
    y0 = margin,
    x1 = w - margin,
    y1 = h - margin;

  if (type === 'grid' || type === 'lined') {
    for (let y = y0; y <= y1 + 0.01; y += s)
      page.drawLine({ start: { x: x0, y }, end: { x: x1, y }, thickness: 0.5, color });
    if (type === 'grid')
      for (let x = x0; x <= x1 + 0.01; x += s)
        page.drawLine({ start: { x, y: y0 }, end: { x, y: y1 }, thickness: 0.5, color });
  } else if (type === 'dot') {
    for (let y = y0; y <= y1 + 0.01; y += s)
      for (let x = x0; x <= x1 + 0.01; x += s) page.drawCircle({ x, y, size: 0.9, color });
  } else if (type === 'isometric') {
    for (let x = x0; x <= x1 + 0.01; x += s)
      page.drawLine({ start: { x, y: y0 }, end: { x, y: y1 }, thickness: 0.5, color });
    const m = Math.tan(Math.PI / 6); // 30 degrees
    drawDiagonals(page, [x0, y0, x1, y1], m, s, color);
    drawDiagonals(page, [x0, y0, x1, y1], -m, s, color);
  }
  // outer border
  page.drawRectangle({
    x: x0,
    y: y0,
    width: x1 - x0,
    height: y1 - y0,
    borderWidth: 0.6,
    borderColor: color,
  });
}

export default function BrowserGraphPaper() {
  const [type, setType] = useState<GraphType>('grid');
  const [spacing, setSpacing] = useState<number>(5);
  const [paper, setPaper] = useState<Paper>('letter');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [color, setColor] = useState<LineColor>('grey');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setError(null);
    setBusy(true);
    try {
      if (!(spacing >= 1 && spacing <= 50)) throw new Error('Spacing must be between 1 and 50 mm.');
      const pdf = await PDFDocument.create();
      let [w, h] = PAPER[paper];
      if (orientation === 'landscape') [w, h] = [h, w];
      drawPage(pdf, type, spacing, w, h, COLORS[color]);
      pdf.setTitle('Graph paper');
      pdf.setProducer('Cleanor · cleanor.app');
      const bytes = Uint8Array.from(await pdf.save());
      triggerDownload(bytes, `cleanor.app-${type}-graph-paper-${spacing}mm.pdf`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not generate the graph paper.');
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--muted, #5b6072)',
  };

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Graph paper generator</h2>
          <p>
            Pick a grid style and spacing, then download a print-ready PDF. Everything is built in
            your browser, so nothing is uploaded.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div
        className="tool-live-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 16,
          marginTop: 8,
        }}
      >
        <label style={fieldStyle}>
          <span style={labelStyle}>Style</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as GraphType)}
            style={{ padding: '8px 10px' }}
          >
            <option value="grid">Square grid</option>
            <option value="dot">Dot grid</option>
            <option value="lined">Lined (ruled)</option>
            <option value="isometric">Isometric</option>
          </select>
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>Spacing (mm)</span>
          <input
            type="number"
            value={spacing}
            min={1}
            max={50}
            step={0.5}
            onChange={(e) => setSpacing(parseFloat(e.target.value))}
            style={{ padding: '8px 10px' }}
          />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>Paper</span>
          <select
            value={paper}
            onChange={(e) => setPaper(e.target.value as Paper)}
            style={{ padding: '8px 10px' }}
          >
            <option value="letter">US Letter</option>
            <option value="a4">A4</option>
          </select>
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>Orientation</span>
          <select
            value={orientation}
            onChange={(e) => setOrientation(e.target.value as Orientation)}
            style={{ padding: '8px 10px' }}
          >
            <option value="portrait">Portrait</option>
            <option value="landscape">Landscape</option>
          </select>
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>Line colour</span>
          <select
            value={color}
            onChange={(e) => setColor(e.target.value as LineColor)}
            style={{ padding: '8px 10px' }}
          >
            <option value="grey">Grey</option>
            <option value="blue">Blue</option>
          </select>
        </label>
      </div>

      <div className="hero-actions tool-live-actions" style={{ marginTop: 18 }}>
        <button className="button" type="button" onClick={generate} disabled={busy}>
          {busy ? 'Building PDF…' : 'Download graph paper PDF'}
        </button>
      </div>
      {error ? <p className="support-feedback support-feedback-error">{error}</p> : null}
    </article>
  );
}
